/**
 * WHY THE MICROPHONE DID NOT START (Build 10i) — one answer per cause.
 *
 * Until 7 Oct 2026 every failure read "allow this site to use the microphone
 * in your browser". On Kelvo's first live voice answer that sent the owner
 * through Chrome's site settings and the Mac's privacy settings — both already
 * allowed — while the real cause was a response header set at the edge for
 * the whole domain (a Cloudflare Transform Rule), which no browser setting can
 * override. One message for five causes sent him to fix the wrong thing.
 *
 * The browser does say which cause it was:
 *   - the PAGE is not allowed the microphone (Permissions-Policy): the error
 *     is NotAllowedError and document.featurePolicy reports false. Nothing
 *     the person can change — say so, so nobody hunts through settings;
 *   - the PERSON (or the browser's site setting, or the computer) refused:
 *     NotAllowedError with the policy allowing it;
 *   - no microphone at all: NotFoundError / OverconstrainedError;
 *   - another app holds it: NotReadableError / AbortError;
 *   - no capture API in this context (an old browser, or not https).
 *
 * featurePolicy exists in Chromium only. Elsewhere it is undefined, and a
 * NotAllowedError is then reported as a refusal — the common case — never as
 * the site's fault, which would be a claim the browser did not make.
 *
 * Pure and React-free, so both recorders (the owner's voice answer and the
 * caller's record page) share it and it is unit-tested directly.
 */

export type MicFailure = 'blocked_by_site' | 'denied' | 'no_microphone' | 'busy' | 'unsupported' | 'other'

/** What the page's Permissions-Policy says about the microphone: true, false,
 *  or undefined when the browser does not expose it (not Chromium). */
export function policyAllowsMicrophone(doc: unknown = (globalThis as { document?: unknown }).document): boolean | undefined {
  const fp = (doc as { featurePolicy?: { allowsFeature?: (f: string) => unknown } } | undefined)?.featurePolicy
  if (!fp || typeof fp.allowsFeature !== 'function') return undefined
  const allowed = fp.allowsFeature('microphone')
  return typeof allowed === 'boolean' ? allowed : undefined
}

/** True when this browser, here, can ask for a microphone at all. */
export function canAskForMicrophone(nav: unknown = (globalThis as { navigator?: unknown }).navigator): boolean {
  const md = (nav as { mediaDevices?: { getUserMedia?: unknown } } | undefined)?.mediaDevices
  return typeof md?.getUserMedia === 'function'
}

/** Which cause, from the error getUserMedia (or the audio graph) threw. */
export function classifyMicFailure(error: unknown, policyAllows: boolean | undefined): MicFailure {
  const name = (error as { name?: unknown } | null)?.name
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return policyAllows === false ? 'blocked_by_site' : 'denied'
    case 'NotFoundError':
    case 'OverconstrainedError':
      return 'no_microphone'
    case 'NotReadableError':
    case 'AbortError':
      return 'busy'
    default:
      return 'other'
  }
}

const MESSAGES: Record<MicFailure, string> = {
  blocked_by_site:
    'This page is not allowed to use the microphone. That is a setting on the website, not in your browser, so there is nothing for you to change. Type your answer for now.',
  denied:
    'The microphone was refused. Allow this site to use it (click the icon to the left of the web address), then try again.',
  no_microphone: 'No microphone was found. Connect one, or use a phone or laptop that has one, then try again.',
  busy: 'Another app is using the microphone. Close it, then try again.',
  unsupported: 'This browser cannot record here. Use an up-to-date Chrome, Safari or Edge.',
  other: 'The microphone could not be started. Try again, or type your answer.',
}

/** The sentence to show for a cause. */
export function micFailureMessage(reason: MicFailure): string {
  return MESSAGES[reason]
}
